// src/features/planning/utils/buildRequirementTree.ts
// ADR-094 Rule MRP-FE-03: the backend returns a flat list (MrpRunSerializer
// serializes `requirements` as a flat array). This pure function turns it
// into a real tree by `parent_requirement`, for indented rendering.
// Exported and unit-testable independently of the React component.

import type { MrpRequirement } from "../types";

export interface RequirementTreeNode {
  requirement: MrpRequirement;
  children: RequirementTreeNode[];
}

export function buildRequirementTree(
  requirements: MrpRequirement[]
): RequirementTreeNode[] {
  const byId = new Map<string, RequirementTreeNode>();
  for (const req of requirements) {
    byId.set(req.id, { requirement: req, children: [] });
  }

  const roots: RequirementTreeNode[] = [];

  for (const req of requirements) {
    const node = byId.get(req.id)!;
    if (req.parent_requirement && byId.has(req.parent_requirement)) {
      byId.get(req.parent_requirement)!.children.push(node);
    } else {
      // no parent, or parent not found in this list (defensive — should
      // not happen since a run's requirements always include their own
      // ancestors) => treat as root
      roots.push(node);
    }
  }

  return roots;
}

/** Flattens the tree back into a depth-annotated list for simple table rendering. */
export interface FlatRequirementRow {
  requirement: MrpRequirement;
  depth: number;
}

export function flattenRequirementTree(
  nodes: RequirementTreeNode[],
  depth = 0
): FlatRequirementRow[] {
  const rows: FlatRequirementRow[] = [];
  for (const node of nodes) {
    rows.push({ requirement: node.requirement, depth });
    rows.push(...flattenRequirementTree(node.children, depth + 1));
  }
  return rows;
}
